import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ReceiptText, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { parasutApi, errMsg, openInvoicePdf } from "../../lib/parasutApi";
import { formFromPrefill, toPayload } from "./invoiceDefaults";
import { CustomerSection, AmountSection, TaxSection, EDocSection, TotalsBar } from "./InvoiceFormSections";
import { InvoiceHistory } from "./InvoiceHistory";
import { SplitSection, LinesEditor, hasNegative } from "./InvoiceLines";

function useInvoicePolling(invoices, setInvoices) {
  const ref = useRef(invoices);
  ref.current = invoices;
  const pending = invoices.some((i) => i.status === "processing");
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(async () => {
      for (const inv of ref.current.filter((i) => i.status === "processing")) {
        try {
          const u = await parasutApi.refresh(inv.id);
          setInvoices((list) => list.map((x) => (x.id === u.id ? u : x)));
          if (u.status === "approved") toast.success(`Fatura onaylandı${u.invoice_no ? ` · ${u.invoice_no}` : ""}`);
          if (u.status === "error") toast.error(`Resmileştirme hatası: ${u.error || ""}`);
        } catch { /* sonraki turda tekrar denenir */ }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [pending, setInvoices]);
}

export function InvoiceDialog({ bookingId, open, onOpenChange }) {
  const [pre, setPre] = useState(null);
  const [form, setForm] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [rates, setRates] = useState(null);
  const editForm = (v) => { setConfirm(false); setForm(v); };
  useInvoicePolling(invoices, setInvoices);

  const load = useCallback(async () => {
    setPre(null); setForm(null); setLoadErr(""); setEditingId(null); setConfirm(false);
    try {
      const [p, s, rt] = await Promise.all([parasutApi.prefill(bookingId), parasutApi.status().catch(() => null), parasutApi.rates().catch(() => null)]);
      setPre(p); setRates(rt); setForm(formFromPrefill(p)); setInvoices(p.invoices || []); setAccounts(s?.accounts || []);
    } catch (e) { setLoadErr(errMsg(e)); }
  }, [bookingId]);
  useEffect(() => { if (open && bookingId) load(); }, [open, bookingId, load]);

  const active = invoices.find((i) => ["processing", "approved", "creating"].includes(i.status));
  const upsert = (inv) => setInvoices((list) => [inv, ...list.filter((x) => x.id !== inv.id)]);

  const submit = async (formalize) => {
    if (formalize && !confirm) { setConfirm(true); return; }
    setSaving(true); setConfirm(false);
    try {
      const body = toPayload(form, formalize);
      const inv = editingId ? await parasutApi.update(editingId, body) : await parasutApi.create(body);
      upsert(inv); setEditingId(null);
      toast.success(formalize ? "Fatura gönderildi, resmileştiriliyor…" : "Taslak Paraşüt'e kaydedildi");
    } catch (e) {
      const inv = e?.response?.data?.detail?.invoice;
      if (inv) { upsert(inv); setEditingId(inv.id); }
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const act = async (inv, fn, okMsg) => {
    setBusyId(inv.id);
    try { const u = await fn(inv.id); if (u) upsert(u); if (okMsg) toast.success(okMsg); return u; }
    catch (e) { toast.error(errMsg(e)); return null; } finally { setBusyId(null); }
  };
  const onCancel = async (inv) => {
    const msg = inv.status === "approved" ? "Resmileşmiş fatura Paraşüt'te İPTAL edilecek. Emin misiniz?" : "Taslak fatura silinecek. Emin misiniz?";
    if (!window.confirm(msg)) return;
    const u = await act(inv, parasutApi.cancel, inv.status === "approved" ? "Fatura iptal edildi · düzeltip yeniden kesebilirsiniz" : "Taslak silindi");
    if (u && inv.payload) { setForm({ ...form, ...inv.payload }); setEditingId(null); }
  };
  const onPdf = async (inv) => {
    setBusyId(inv.id);
    try { if (!(await openInvoicePdf(inv.id))) toast.info("PDF hazırlanıyor, birkaç saniye sonra tekrar deneyin"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };
  const onEdit = (inv) => { setForm({ ...form, ...inv.payload, split: inv.payload?.split || form.split, pax: inv.payload?.pax || form.pax }); setEditingId(inv.id); setConfirm(false); };

  const b = pre?.booking;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white p-0 gap-0">
        <DialogHeader className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-slate-900"><ReceiptText size={18} className="text-indigo-600" /> Fatura Kes · Paraşüt</DialogTitle>
          <DialogDescription data-testid="inv-booking-info" className="text-[12px]">
            {b ? `${b.confirmation_code || "—"} · ${b.customer_name || ""} · ${b.booking_date || ""} · ${b.total_pax || 0} kişi · ${pre.channel}` : "Rezervasyon yükleniyor…"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 px-5 py-4">
          {loadErr && <div data-testid="inv-load-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{loadErr}</div>}
          {!form && !loadErr && <div className="flex items-center gap-2 py-10 justify-center text-slate-500"><Loader2 className="animate-spin" size={18} /> Hazırlanıyor…</div>}
          <InvoiceHistory invoices={invoices} busyId={busyId} onPdf={onPdf} onEdit={onEdit} onCancel={onCancel}
            onFormalize={(i) => act(i, parasutApi.formalize, "Resmileştirme başlatıldı")}
            onRefresh={(i) => act(i, parasutApi.refresh)} />
          {form && active && (
            <div data-testid="inv-active-banner" className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-800">
              <CheckCircle2 size={16} /> Bu rezervasyonun geçerli faturası var. Değiştirmek için önce iptal edin; form tekrar açılır.
            </div>
          )}
          {form && !active && (<>
            {editingId && <div data-testid="inv-editing-banner" className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">Kayıt #{editingId} düzenleniyor — kaydettiğinizde taslak yenilenir.</div>}
            <CustomerSection f={form} set={editForm} countryInfo={pre?.country_info} foreignTax={pre?.rules?.foreign_tax_number} />
            <AmountSection f={form} set={editForm} rateDate={pre?.rate_date} rateSource={pre?.rate_source} rates={rates} />
            <SplitSection f={form} set={editForm} rules={pre.rules} amountSource={pre?.amount_source} bookingAmount={pre?.amount} />
            <LinesEditor f={form} set={editForm} bookingAmount={pre?.amount} />
            <TaxSection f={form} set={editForm} />
            <EDocSection f={form} set={editForm} accounts={accounts} />
            <TotalsBar f={form} />
            <div className="sticky bottom-0 -mx-5 -mb-4 flex flex-wrap items-center justify-end gap-2 border-t bg-white/95 backdrop-blur px-5 py-3">
              {confirm && <span data-testid="inv-confirm-text" className="mr-auto text-[12px] font-semibold text-red-600">Resmi e-belge kesilecek. Onaylıyor musunuz?</span>}
              {saving && <span data-testid="inv-saving-text" className="mr-auto text-[12px] font-medium text-slate-500">Paraşüt'e gönderiliyor (müşteri, kalemler, fatura)… 10-20 sn sürebilir</span>}
              <button data-testid="inv-save-draft" disabled={saving || hasNegative(form)} onClick={() => submit(false)}
                className="h-9 px-4 rounded-full border border-slate-300 text-[13px] font-semibold text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50">Taslak Kaydet</button>
              <button data-testid="inv-submit" disabled={saving || hasNegative(form)} onClick={() => submit(true)}
                className={`h-9 px-5 rounded-full text-[13px] font-bold text-white shadow transition-colors disabled:opacity-50 ${confirm ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}`}>
                {saving ? <Loader2 size={14} className="animate-spin inline" /> : confirm ? "Evet, Faturayı Kes" : "Faturayı Kes"}
              </button>
            </div>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
